import { Button, Tabs } from "@mantine/core";
import * as Sentry from "@sentry/browser";
import { useEffect } from "react";
import type { FieldErrors, UseFormReturn } from "react-hook-form";
import { useLocation, useSearchParams } from "react-router";
import { useMissingData } from "../../../hooks/useMissingData";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { TabWithHighlightAndError } from "../../../mantine/components/TabWithHighlightAndError/TabWithHighlightAndError";
import { YesNoChoice } from "../../../types/api.types";
import type { Person } from "../../../utils/backend-types";
import { ObjectName } from "../../../utils/enums";
import { buildFieldNameToLabelMap } from "../../../utils/react-hook-form/buildFieldNameToLabelMap";
import { countErrorsForTab } from "../../BuildingBlocks/Forms/utils/countErrorsForTab";
import { countMissingDataInTab } from "../../BuildingBlocks/Forms/utils/countMissingDataInTab";
import { findFirstTabWithError } from "../../BuildingBlocks/Forms/utils/findFirstTabWithError";
import { HistoryTab } from "../../HistoryTab/HistoryTab";
import {
  GENERATOR_EDIT_TABS,
  GENERATOR_FORM_DATA
} from "./GeneratorModal.constants";
import type { GeneratorSingleEndpointResponse } from "./GeneratorModal.types";
import {
  EEGAnlageTab,
  getEegTabIgnoredMissingFields
} from "./Tabs/EEGAnlageTab";
import { GeneratorGeneralTab } from "./Tabs/GeneratorGeneralTab";
import { KwkAnlageTab } from "./Tabs/KwkAnlageTab";
import { PriceDataTab } from "./Tabs/PriceDataTab";
import { StromStgTab } from "./Tabs/StromStgTab";

const STROMSTG_CHOICE_FIELDS: Array<string> = [
  "anlage_stromstg.stfrei_paragraph_9_abs_1_nr_1_stromstg",
  "anlage_stromstg.einzelerlaubnis_steuerfreie_entnahme_zur_stromerzeugung",
  "anlage_stromstg.stromstg_paragraph_9_1_3a",
  "anlage_stromstg.stromstg_paragraph_9_1_3b"
];

function replaceCircular() {
  const seen = new WeakSet<object>();
  return (key: string, value: unknown) => {
    if (key === "ref") return "[ref]"; // skip DOM refs (avoids circular DOM tree)
    if (value instanceof Element || value instanceof Node) {
      return `[Node ${(value as Element).tagName ?? value.nodeName}]`;
    }
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) return "[Circular]";
      seen.add(value);
    }
    return value;
  };
}

export interface GeneratorModalFormProps {
  generatorForm: UseFormReturn<GeneratorSingleEndpointResponse>;
  generatorId: number;
  variantId: number; // only for standortübergreifende verklammerung
  isPremium: boolean;
  persons: Array<Person>;
  showPriceData: boolean;
  onSubmit: (data: GeneratorSingleEndpointResponse) => void;
  isPending?: boolean;
  generator?: GeneratorSingleEndpointResponse;
  siteId?: number;
  onClose?: () => void;
  isCommissioningNotRequired?: boolean;
}

function GeneratorModalForm({
  generatorForm,
  generatorId,
  variantId,
  isPremium,
  persons,
  showPriceData,
  onSubmit,
  isPending,
  generator,
  siteId,
  onClose,
  isCommissioningNotRequired = false
}: GeneratorModalFormProps) {
  const fieldNameToLabelMap = buildFieldNameToLabelMap(GENERATOR_FORM_DATA);
  const {
    handleSubmit,
    formState: { dirtyFields, errors, submitCount }
  } = generatorForm;

  const hasEEGAnlage = generator?.einheit?.eeg_anlage;
  const hasKWKAnlage = generator?.einheit?.kwk_anlage;
  const hasStromstg = generator?.anlage_stromstg;
  const [searchParams, setSearchParams] = useSearchParams();
  const { state: locationState } = useLocation();

  const tab = searchParams.get("tab") || GENERATOR_EDIT_TABS.TAB_GENERAL;
  const setTab = (newTab: string | null) => {
    if (newTab === null) {
      return;
    }
    searchParams.set("tab", newTab);
    setSearchParams(searchParams, { state: locationState });
  };

  const { missingData } = useMissingData();
  const foerderregime = generatorForm.watch("einheit.eeg_anlage.foerderregime");
  const eegTabIgnoredMissingFields =
    getEegTabIgnoredMissingFields(foerderregime);

  const getValidTab = () => {
    if (hasEEGAnlage && tab === GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA) {
      return GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA;
    }

    if (hasKWKAnlage && tab === GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA) {
      return GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA;
    }

    if (hasStromstg && tab === GENERATOR_EDIT_TABS.TAB_STROMSTEUER_DATA) {
      return GENERATOR_EDIT_TABS.TAB_STROMSTEUER_DATA;
    }

    if (showPriceData && tab === GENERATOR_EDIT_TABS.TAB_PRICE_DATA) {
      return GENERATOR_EDIT_TABS.TAB_PRICE_DATA;
    }

    if (tab === GENERATOR_EDIT_TABS.TAB_HISTORY) {
      return GENERATOR_EDIT_TABS.TAB_HISTORY;
    }

    return GENERATOR_EDIT_TABS.TAB_GENERAL;
  };

  const validTab = getValidTab();

  /* hunting down https://node-energy.atlassian.net/browse/WUP-9704: records how a
     Stromsteuer answer reached its current value, so a captured event shows whether
     the user picked it or it was replaced wholesale. */
  useEffect(() => {
    const subscription = generatorForm.watch((values, { name, type }) => {
      const isChoiceChange = !!name && STROMSTG_CHOICE_FIELDS.includes(name);
      // no field name means every value was replaced at once: either a
      // useForm({ values }) resync from a refetch, or reset() on modal close
      const isWholeFormUpdate = !name;

      if (!isChoiceChange && !isWholeFormUpdate) {
        return;
      }

      const anlageStromstg = values.anlage_stromstg;

      Sentry.addBreadcrumb({
        category: "wup-9704",
        level: "info",
        message: name
          ? `Stromsteuer choice changed: ${name}`
          : "Form values replaced (resync or reset)",
        data: {
          changedField: name ?? null,
          eventType: type ?? null,
          choices: JSON.stringify({
            "911":
              anlageStromstg?.stfrei_paragraph_9_abs_1_nr_1_stromstg ?? null,
            Einzelerlaubnis:
              anlageStromstg?.einzelerlaubnis_steuerfreie_entnahme_zur_stromerzeugung ??
              null,
            "913a": anlageStromstg?.stromstg_paragraph_9_1_3a ?? null,
            "913b": anlageStromstg?.stromstg_paragraph_9_1_3b ?? null
          })
        }
      });
    });

    return () => subscription.unsubscribe();
  }, [generatorForm]);

  function log() {
    const values = generatorForm.getValues();
    const errorPaths = Object.keys(GENERATOR_FORM_DATA).filter((path) => {
      let cursor: unknown = errors;
      for (const segment of path.split(".")) {
        if (!cursor || typeof cursor !== "object") return false;
        cursor = (cursor as Record<string, unknown>)[segment];
      }
      return !!cursor;
    });
    let errorsJson: string;
    try {
      errorsJson = JSON.stringify(errors, replaceCircular(), 2);
    } catch (e) {
      errorsJson = `[JSON.stringify threw: ${String(e)}]`;
    }

    const sentryPayload = {
      extra: {
        formData: JSON.stringify(values, replaceCircular()),
        anlageStromstgData: JSON.stringify(
          values.anlage_stromstg ?? null,
          replaceCircular()
        ),
        formErrors: JSON.stringify(errors, replaceCircular()),
        errorsJson,
        errorPaths,
        submitCount,
        dirtyFields: JSON.stringify(dirtyFields, replaceCircular()),
        currentTab: tab,
        validTab,
        hasEEGAnlage: !!hasEEGAnlage,
        hasKWKAnlage: !!hasKWKAnlage,
        hasStromstg: !!hasStromstg
      }
    };

    const choice911 = generatorForm.getValues(
      "anlage_stromstg.stfrei_paragraph_9_abs_1_nr_1_stromstg"
    );
    const choiceEinzelerlaubnis = generatorForm.getValues(
      "anlage_stromstg.einzelerlaubnis_steuerfreie_entnahme_zur_stromerzeugung"
    );
    const choice913a = generatorForm.getValues(
      "anlage_stromstg.stromstg_paragraph_9_1_3a"
    );
    const choice913b = generatorForm.getValues(
      "anlage_stromstg.stromstg_paragraph_9_1_3b"
    );

    if (
      choice911 !== YesNoChoice.yes &&
      errorPaths.includes(
        "anlage_stromstg.datum_erlaubnis_stfrei_paragraph_9_abs_1_nr_1_stromstg"
      )
    ) {
      Sentry.captureMessage(
        "Generator couldn't be saved (and showed no error message) for 911",
        sentryPayload
      );
    }

    if (
      choiceEinzelerlaubnis !== YesNoChoice.yes &&
      errorPaths.includes(
        "anlage_stromstg.datum_einzelerlaubnis_steuerfreie_entnahme_zur_stromerzeugung"
      )
    ) {
      Sentry.captureMessage(
        "Generator couldn't be saved (and showed no error message) for Einzelerlaubnis",
        sentryPayload
      );
    }

    if (
      choice913a !== YesNoChoice.yes &&
      errorPaths.includes("anlage_stromstg.zeitraum_stromstg_paragraph_9_1_3a")
    ) {
      Sentry.captureMessage(
        "Generator couldn't be saved (and showed no error message) for 913a",
        sentryPayload
      );
    }

    if (
      choice913b !== YesNoChoice.yes &&
      errorPaths.includes("anlage_stromstg.zeitraum_stromstg_paragraph_9_1_3b")
    ) {
      Sentry.captureMessage(
        "Generator couldn't be saved (and showed no error message) for 913b",
        sentryPayload
      );
    }
  }

  function onError(errors: FieldErrors<GeneratorSingleEndpointResponse>) {
    log(); /* hunting down https://node-energy.atlassian.net/browse/WUP-9704 */

    const tabOrder = Object.values(GENERATOR_EDIT_TABS);
    const firstTabWithError = findFirstTabWithError(
      errors,
      GENERATOR_FORM_DATA,
      tabOrder
    );

    if (firstTabWithError) {
      setTab(firstTabWithError);
    }
  }

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit, onError)}>
      <Tabs value={validTab} onChange={setTab}>
        <Tabs.List mb="md">
          <TabWithHighlightAndError
            highlighted={
              !!countMissingDataInTab(
                GENERATOR_EDIT_TABS.TAB_GENERAL,
                GENERATOR_FORM_DATA,
                missingData
              )
            }
            numErrors={countErrorsForTab(
              GENERATOR_EDIT_TABS.TAB_GENERAL,
              GENERATOR_FORM_DATA,
              errors
            )}
            value={GENERATOR_EDIT_TABS.TAB_GENERAL}
          >
            Allgemeine Daten
          </TabWithHighlightAndError>
          {hasEEGAnlage && (
            <TabWithHighlightAndError
              highlighted={
                !!countMissingDataInTab(
                  GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA,
                  GENERATOR_FORM_DATA,
                  missingData,
                  eegTabIgnoredMissingFields
                )
              }
              numErrors={countErrorsForTab(
                GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA,
                GENERATOR_FORM_DATA,
                errors
              )}
              value={GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA}
            >
              Daten zur EEG-Anlage
            </TabWithHighlightAndError>
          )}
          {hasKWKAnlage && (
            <TabWithHighlightAndError
              highlighted={
                !!countMissingDataInTab(
                  GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA,
                  GENERATOR_FORM_DATA,
                  missingData
                )
              }
              numErrors={countErrorsForTab(
                GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA,
                GENERATOR_FORM_DATA,
                errors
              )}
              value={GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA}
            >
              Daten zur KWK-Anlage
            </TabWithHighlightAndError>
          )}
          {hasStromstg && (
            <TabWithHighlightAndError
              highlighted={
                !!countMissingDataInTab(
                  GENERATOR_EDIT_TABS.TAB_STROMSTEUER_DATA,
                  GENERATOR_FORM_DATA,
                  missingData
                )
              }
              numErrors={countErrorsForTab(
                GENERATOR_EDIT_TABS.TAB_STROMSTEUER_DATA,
                GENERATOR_FORM_DATA,
                errors
              )}
              value={GENERATOR_EDIT_TABS.TAB_STROMSTEUER_DATA}
            >
              Stromsteuerliche Anlage
            </TabWithHighlightAndError>
          )}
          {showPriceData && (
            <TabWithHighlightAndError
              highlighted={
                !!countMissingDataInTab(
                  GENERATOR_EDIT_TABS.TAB_PRICE_DATA,
                  GENERATOR_FORM_DATA,
                  missingData
                )
              }
              numErrors={countErrorsForTab(
                GENERATOR_EDIT_TABS.TAB_PRICE_DATA,
                GENERATOR_FORM_DATA,
                errors
              )}
              value={GENERATOR_EDIT_TABS.TAB_PRICE_DATA}
            >
              Preisdaten
            </TabWithHighlightAndError>
          )}
          <TabWithHighlightAndError value={GENERATOR_EDIT_TABS.TAB_HISTORY}>
            Historie
          </TabWithHighlightAndError>
        </Tabs.List>
        <Tabs.Panel value={GENERATOR_EDIT_TABS.TAB_GENERAL}>
          <GeneratorGeneralTab
            generatorForm={generatorForm}
            isCommissioningNotRequired={isCommissioningNotRequired}
            isPremium={isPremium}
            persons={persons}
            siteId={siteId}
          />
        </Tabs.Panel>
        {hasEEGAnlage && (
          <Tabs.Panel value={GENERATOR_EDIT_TABS.TAB_EEG_ANLAGE_DATA}>
            <EEGAnlageTab generatorForm={generatorForm} variantId={variantId} />
          </Tabs.Panel>
        )}
        {hasKWKAnlage && (
          <Tabs.Panel value={GENERATOR_EDIT_TABS.TAB_KWK_ANLAGE_DATA}>
            <KwkAnlageTab generatorForm={generatorForm} />
          </Tabs.Panel>
        )}
        {hasStromstg && (
          <Tabs.Panel value={GENERATOR_EDIT_TABS.TAB_STROMSTEUER_DATA}>
            <StromStgTab
              anlageStromstgId={generator?.anlage_stromstg?.id}
              generatorForm={generatorForm}
              variantId={variantId}
            />
          </Tabs.Panel>
        )}
        {showPriceData && (
          <Tabs.Panel value={GENERATOR_EDIT_TABS.TAB_PRICE_DATA}>
            <PriceDataTab generatorForm={generatorForm} />
          </Tabs.Panel>
        )}
        <Tabs.Panel value={GENERATOR_EDIT_TABS.TAB_HISTORY}>
          <HistoryTab
            entityId={generatorId}
            entityType={ObjectName.Generator}
            fieldNameToLabelMap={fieldNameToLabelMap}
          />
        </Tabs.Panel>
      </Tabs>
      <ModalFooter>
        <Button variant="default" onClick={onClose}>
          Abbrechen
        </Button>
        <Button loading={isPending} type="submit">
          Speichern
        </Button>
      </ModalFooter>
    </form>
  );
}

export { GeneratorModalForm };
